/**
 * 判断当前设备是否为移动端设备（包括智能手机和平板）
 * @returns {boolean} true 表示移动设备，false 表示桌面设备
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  // 1. 检测触摸支持（移动设备通常支持触摸）
  const hasTouchSupport =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  // 2. 检测用户代理字符串中的移动设备关键词
  const mobileAgents = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
    /Mobile/i,
  ];

  const isMobileAgent = mobileAgents.some((regex) =>
    navigator.userAgent.match(regex),
  );

  // 3. 特殊检测：iPad在桌面模式下会被识别为Mac，需要额外检查
  const isMacPlatform = navigator.platform.includes("Mac");
  const isiPad = navigator.maxTouchPoints === 5 && isMacPlatform;

  // 移动设备条件：支持触摸且检测到移动端UA，或是iPad
  return (hasTouchSupport && isMobileAgent) || isiPad;
};

// 设备类型枚举
export enum DeviceType {
  DESKTOP = "desktop",
  MOBILE = "mobile",
  TABLET = "tablet",
  UNKNOWN = "unknown",
}

// 操作系统类型枚举
enum OS {
  WINDOWS = "windows",
  MACOS = "macos",
  IOS = "ios",
  ANDROID = "android",
  LINUX = "linux",
  UNKNOWN = "unknown",
}

// 浏览器类型枚举
enum Browser {
  CHROME = "chrome",
  FIREFOX = "firefox",
  SAFARI = "safari",
  EDGE = "edge",
  OPERA = "opera",
  IE = "ie",
  UNKNOWN = "unknown",
}

/**
 * 检测设备类型（手机、平板、桌面）
 * @returns DeviceType 枚举值
 */
export function detectDeviceType(): DeviceType {
  const userAgent = navigator.userAgent.toLowerCase();
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const pixelRatio = window.devicePixelRatio || 1;

  // 1. 检测平板设备
  const isTablet =
    // iPad 检测
    userAgent.includes("ipad") ||
    // Android 平板检测
    (userAgent.includes("android") &&
      !userAgent.includes("mobile") &&
      screenWidth >= 600) ||
    // 其他平板特征
    (screenWidth >= 600 &&
      screenWidth <= 1280 &&
      screenHeight >= 800 &&
      screenHeight <= 1600 &&
      "ontouchstart" in window &&
      pixelRatio >= 1.5);

  // 2. 检测手机设备
  const isMobile =
    // 常见手机UA标识
    /iphone|ipod|android.*mobile|windows phone|blackberry|iemobile|opera mini/i.test(
      userAgent,
    ) ||
    // 小屏幕设备
    (screenWidth < 600 && "ontouchstart" in window);

  // 3. 返回结果
  if (isTablet) return DeviceType.TABLET;
  if (isMobile) return DeviceType.MOBILE;

  // 4. 默认为桌面设备
  return DeviceType.DESKTOP;
}

/**
 * 检测操作系统类型
 * @returns OS 枚举值
 */
export function detectOS(): OS {
  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes("win")) return OS.WINDOWS;
  if (userAgent.includes("mac")) return OS.MACOS;
  if (
    userAgent.includes("iphone") ||
    userAgent.includes("ipad") ||
    userAgent.includes("ipod")
  )
    return OS.IOS;
  if (userAgent.includes("android")) return OS.ANDROID;
  if (userAgent.includes("linux")) return OS.LINUX;

  return OS.UNKNOWN;
}

/**
 * 检测浏览器类型
 * @returns Browser 枚举值
 */
export function detectBrowser(): Browser {
  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes("chrome") && !userAgent.includes("edg/"))
    return Browser.CHROME;
  if (userAgent.includes("firefox")) return Browser.FIREFOX;
  if (userAgent.includes("safari") && !userAgent.includes("chrome"))
    return Browser.SAFARI;
  if (userAgent.includes("edg/")) return Browser.EDGE;
  if (userAgent.includes("opera") || userAgent.includes("opr/"))
    return Browser.OPERA;
  if (userAgent.includes("msie") || userAgent.includes("trident/"))
    return Browser.IE;

  return Browser.UNKNOWN;
}

/**
 * 获取设备详细信息
 * @returns 包含设备信息的对象
 */
export function getDeviceInfo(): {
  deviceType: DeviceType;
  os: OS;
  browser: Browser;
  screen: { width: number; height: number; pixelRatio: number };
  touchSupport: boolean;
  userAgent: string;
  isHandyApp: boolean;
} {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      deviceType: DeviceType.UNKNOWN,
      os: OS.UNKNOWN,
      browser: Browser.UNKNOWN,
      screen: { width: 0, height: 0, pixelRatio: 1 },
      touchSupport: false,
      userAgent: "",
      isHandyApp: false,
    };
  }
  return {
    deviceType: detectDeviceType(),
    os: detectOS(),
    browser: detectBrowser(),
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      pixelRatio: window.devicePixelRatio || 1,
    },
    touchSupport: "ontouchstart" in window,
    userAgent: navigator.userAgent,
    isHandyApp: isHandyApp(),
  };
}

export const getDeviceUa = () => navigator.userAgent;

export const IsiOS = () => {
  return /iPad|iPhone|iPod/.test(getDeviceUa());
};

/**
 * 检测是否为鸿蒙系统
 * @returns {boolean} 是否为鸿蒙系统
 */
export function isHarmonyOS() {
  const ua = navigator.userAgent;
  // 主要检测标识
  const harmonyPatterns = [
    /HarmonyOS/i, // 鸿蒙系统标识
    /HMOS/i, // 鸿蒙系统缩写
    /HUAWEI/i, // 华为设备
    /EMUI\/(\d+)/i, // 华为EMUI系统
  ];

  // 检测鸿蒙标识
  const isHarmony = harmonyPatterns.some((pattern) => pattern.test(ua));

  return isHarmony;
}

/**
 * 检测终端系统类型
 * @returns 移动端返回 'android' | 'ios' | 'harmonyos'，非移动端返回 'windows' | 'mac'
 */
export function detectOSType():
  | "android"
  | "ios"
  | "harmonyos"
  | "windows"
  | "mac" {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;

  // 首先判断是否为移动设备[1,3](@ref)
  const isMobile =
    /(Android|iPhone|iPad|iPod|iOS|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i.test(
      userAgent,
    );

  if (isMobile) {
    // 移动端检测[2,4,7](@ref)

    // 优先检测鸿蒙系统[7](@ref)
    if (/HarmonyOS|OpenHarmony/i.test(userAgent)) {
      return "harmonyos";
    }

    // 检测Android
    if (/Android/i.test(userAgent)) {
      return "android";
    }

    // 检测iOS设备[2](@ref)
    if (/iPhone|iPad|iPod|iOS/i.test(userAgent)) {
      return "ios";
    }
  }

  // 非移动端检测[2](@ref)
  const macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K", "macOS"];
  const windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"];

  if (macosPlatforms.includes(platform)) {
    return "mac";
  }

  if (windowsPlatforms.includes(platform)) {
    return "windows";
  }

  // 默认返回windows（大多数非移动端设备）
  return "windows";
}

export const isBrowser = () => typeof window !== "undefined";
export const isHandyApp = (userAgent?: string) => {
  if (userAgent) {
    return /BBL-App/i.test(userAgent);
  }
  return isBrowser() && /BBL-App/i.test(window.navigator.userAgent);
};

/**
 * @returns handy app version
 */
export const getHandyAppVersion = () => {
  const ua = window.navigator.userAgent;
  const match = ua.match(/BBL-APP\/(\d+)/i);
  return match ? match[1] : null;
};

/**
 * 将 locale 标识转为语言代码
 * cn / zh / zh-cn / zh-tw 视为中文，其余一律返回 en
 */
export const localeToLanguage = (locale: string): string => {
  const CN_LOCALES = ["cn", "zh", "zh-cn", "zh-tw"];
  return CN_LOCALES.includes(locale.toLowerCase()) ? "cn" : "en";
};

export const getLangByUaString = (userAgentUA = "") => {
  const lang = /BBL-APP-LANGUAGE\/(\w+)( .+)?/i.exec(userAgentUA);
  if (lang && lang[1]) {
    return localeToLanguage(lang[1]);
  }
  return "";
};
